'use client';

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import type { Countdown } from './countdown';
import { remainingSeconds, running, secondsLeft } from './countdown';
import { monotonicNow } from './date';
import { useUnthrottledInterval } from './timers';

/**
 * How often the countdown asks the server how much time is really left, while the user is
 * looking at it. Drift inside one interval is only ever a second or two, and the two cases
 * that produce more than that — a clock that was changed, a device that slept — are caught
 * the moment the tab comes back rather than waited for.
 */
const TIME_SYNC_INTERVAL_MS = 30000;

/** The least time that has to have passed since the last answer before asking again. */
const SYNC_COOLDOWN_MS = 5000;

/** How far the wall clock has to run ahead of the monotonic one to mean a suspend. */
const SLEPT_THRESHOLD_MS = 5000;

/**
 * A sync that took longer than this is thrown away rather than acted on: the further
 * apart the two ends of the round trip are, the less its midpoint says about when the
 * server actually read its clock. The countdown keeps running on its own until a quicker
 * answer comes back.
 */
const MAX_SYNC_ROUND_TRIP_MS = 5000;

/**
 * A correction this small, in either direction, is ignored outright. The midpoint of a
 * round trip only pins the server's reading down to within the trip's variance, so at
 * this size it is impossible to tell real drift from the network answering at a slightly
 * different pace — and acting on noise is what makes the seconds on screen stutter at
 * every poll. Drift that is real keeps growing until it clears this band and is acted on.
 */
const SYNC_TOLERANCE_MS = 1000;

/**
 * A correction this large is applied in one go. A gap this big means the device slept or
 * its clock stopped, and being right immediately matters more than moving smoothly
 * through seconds the user never really had.
 */
const SYNC_STEP_MS = 5000;

/**
 * How much of a correction between those two sizes each tick works off. Easing it in a
 * quarter-second at a time makes a second on screen occasionally run a touch short or
 * long, instead of the whole display skipping when the answer lands mid-second.
 */
const SYNC_SLEW_MS = 250;

type SyncedCountdownOptions = {
  /** Zero is not a countdown at all: nothing is timed and nothing ever expires. */
  timeInSeconds: number;
  /**
   * Called once, on the tick that finds no time left. The countdown has already stopped
   * itself by then; what running out means is the caller's to decide.
   */
  onExpire: () => void;
  /** Answers with the server's remaining seconds, which the countdown eases itself towards. */
  onSyncTime?: () => Promise<number | void>;
  /** Told the countdown was paused. Rejecting puts it back to running, since it never stopped there. */
  onPause?: () => Promise<void>;
  /** Answers with the remaining seconds to carry on from. Rejecting leaves the countdown paused. */
  onResume?: () => Promise<number | void>;
};

/**
 * A countdown that keeps its own time against the monotonic clock, corrects itself
 * against a server that has the last word, and can be paused, resumed and stopped.
 *
 * It re-renders its caller once a second while running, so the seconds on screen can be
 * read straight off `secondsLeft`.
 */
export function useSyncedCountdown({
  timeInSeconds,
  onExpire,
  onSyncTime,
  onPause,
  onResume,
}: SyncedCountdownOptions) {
  const [countdown, setCountdown] = useState<Countdown>(() =>
    timeInSeconds === 0
      ? { status: 'stopped', remainingInSeconds: 0 }
      : running(timeInSeconds),
  );

  const isSyncingTimeRef = useRef(false);
  const lastSyncedAtRef = useRef(-Infinity);
  const clocksRef = useRef({ wall: Date.now(), monotonic: monotonicNow() });
  const [isSyncingPause, setIsSyncingPause] = useState(false);

  // A countdown that has been stopped is done, and a later time limit does not start it
  // again — the caller stops it when whatever was being timed is over.
  const isStoppedRef = useRef(false);

  // The seconds on screen are read off the countdown as it renders, so there is nothing
  // to keep in step with it; a tick only has to ask for another render.
  const [, render] = useReducer((renders: number) => renders + 1, 0);

  // Picks up a time limit that arrives after the first render.
  useEffect(() => {
    if (isStoppedRef.current || timeInSeconds === 0) {
      return;
    }

    setCountdown(running(timeInSeconds));
  }, [timeInSeconds]);

  const stop = useCallback(() => {
    isStoppedRef.current = true;

    setCountdown((current) => ({
      status: 'stopped',
      remainingInSeconds: remainingSeconds(current),
    }));
  }, []);

  const syncTime = useCallback(async () => {
    if (!onSyncTime || isSyncingTimeRef.current) {
      return;
    }

    isSyncingTimeRef.current = true;

    try {
      const requestedAt = monotonicNow();
      const serverRemaining = await onSyncTime();
      const roundTrip = monotonicNow() - requestedAt;

      if (roundTrip > MAX_SYNC_ROUND_TRIP_MS) {
        return;
      }

      lastSyncedAtRef.current = Date.now();

      if (typeof serverRemaining !== 'number') {
        return;
      }

      // The server read its clock partway through the round trip rather than when the
      // answer landed, so counting from the answer would hand back however long the
      // network took — a different amount of time every poll, and always in the user's
      // favour. Counting from the middle of the round trip is the closest the client can
      // get to the instant the server was talking about.
      const syncedDeadline =
        requestedAt + roundTrip / 2 + serverRemaining * 1000;

      setCountdown((current) => {
        if (current.status !== 'running') {
          return current;
        }

        // Out of time on the server is fact, not estimate — stepped straight onto
        // rather than eased in, so the next tick expires.
        if (serverRemaining <= 0) {
          return { status: 'running', deadline: syncedDeadline };
        }

        return { ...current, syncedDeadline };
      });
    } catch {
      // The countdown carries on with what it has and the next poll asks again.
    } finally {
      isSyncingTimeRef.current = false;
    }
  }, [onSyncTime]);

  const syncIfDue = useCallback(() => {
    if (Date.now() - lastSyncedAtRef.current < SYNC_COOLDOWN_MS) {
      return;
    }

    void syncTime();
  }, [syncTime]);

  const syncIfDueRef = useRef(syncIfDue);

  useEffect(() => {
    syncIfDueRef.current = syncIfDue;
  }, [syncIfDue]);

  const slept = useCallback(() => {
    const wall = Date.now();
    const monotonic = monotonicNow();
    const previous = clocksRef.current;

    clocksRef.current = { wall, monotonic };

    return (
      wall - previous.wall - (monotonic - previous.monotonic) >
      SLEPT_THRESHOLD_MS
    );
  }, []);

  const isRunning = countdown.status === 'running';
  const canSync = onSyncTime !== undefined;

  useUnthrottledInterval(
    () => {
      // Worked off through the updater so ticks that pile up before a render — a busy
      // main thread, a burst of caught-up worker ticks — still each move the deadline on
      // from where the one before left it rather than from a stale render.
      setCountdown((current) => {
        if (
          current.status !== 'running' ||
          current.syncedDeadline === undefined
        ) {
          return current;
        }

        const drift = current.syncedDeadline - current.deadline;

        if (Math.abs(drift) <= SYNC_TOLERANCE_MS) {
          return { status: 'running', deadline: current.deadline };
        }

        if (Math.abs(drift) >= SYNC_STEP_MS) {
          return { status: 'running', deadline: current.syncedDeadline };
        }

        return {
          ...current,
          deadline: current.deadline + Math.sign(drift) * SYNC_SLEW_MS,
        };
      });

      if (remainingSeconds(countdown) <= 0) {
        // Stopped before the caller hears about it, so ticks that land before React has
        // re-rendered — and torn the interval down — cannot report the same expiry twice.
        if (isStoppedRef.current) {
          return;
        }

        stop();
        onExpire();
      } else {
        render();
      }
    },
    isRunning ? 1000 : null,
  );

  useUnthrottledInterval(
    () => {
      if (document.visibilityState === 'hidden' && !slept()) {
        return;
      }

      syncIfDue();
    },
    isRunning && canSync ? TIME_SYNC_INTERVAL_MS : null,
  );

  useEffect(() => {
    if (!isRunning || !canSync) {
      return;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      syncIfDueRef.current();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isRunning, canSync]);

  const pause = useCallback(async () => {
    if (countdown.status !== 'running') {
      return;
    }

    const localRemaining = Math.max(remainingSeconds(countdown), 0);

    // Freeze the countdown right away so the pause feels instant. The server has the
    // last word on how much time is left, but it gives it back on resume.
    setCountdown({ status: 'paused', remainingInSeconds: localRemaining });

    if (!onPause) {
      return;
    }

    setIsSyncingPause(true);

    try {
      await onPause();
    } catch {
      // Still running on the server, so it must keep running here.
      setCountdown(running(localRemaining));
    } finally {
      setIsSyncingPause(false);
    }
  }, [countdown, onPause]);

  const resume = useCallback(async () => {
    if (countdown.status !== 'paused') {
      return;
    }

    let remaining = countdown.remainingInSeconds;

    if (onResume) {
      setIsSyncingPause(true);

      try {
        const serverRemaining = await onResume();

        if (typeof serverRemaining === 'number') {
          remaining = serverRemaining;
        }
      } catch {
        // Still paused on the server, so stay paused here too.
        return;
      } finally {
        setIsSyncingPause(false);
      }
    }

    setCountdown(running(remaining));
  }, [countdown, onResume]);

  return {
    status: countdown.status,
    /**
     * The whole seconds to show, or nothing at all when there is no time to report — an
     * untimed caller has no clock to put on screen, as opposed to one whose clock has
     * run down to zero.
     */
    secondsLeft: timeInSeconds === 0 ? undefined : secondsLeft(countdown),
    /** What is left, in whole seconds, for a caller that has to report it rather than show it. */
    remaining: () => Math.max(remainingSeconds(countdown), 0),
    /** A pause or resume is waiting on the server. */
    isSyncing: isSyncingPause,
    stop,
    pause,
    resume,
  };
}
